import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { CameraIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";

import openInNewTab from "../../utils/openInNewTab";
import formatDateTime from "../../utils/formatDateTime";
import PagedTable from "../../components/shared/Tables/PagedTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import PartProMessageChain from "../../components/partProMessaging/PartProMessageChain";
import { IPartProConversation } from "../../models/PartProMessage";

const colHelper = createColumnHelper<IPartProConversation>();

const ThumbnailCell = ({ conversation }: { conversation: IPartProConversation }) => {
  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  if (!conversation.partImageUrl) {
    return <CameraIcon className="w-14 text-gray-400" />;
  }

  return (
    <>
      <span
        className="flex justify-center relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
      >
        <img
          className="min-w-16 max-w-28 h-auto object-contain rounded border border-gray-200"
          src={conversation.partImageUrl}
          alt={conversation.partTagNumber ?? "Part"}
        />
      </span>

      {hovered && (
        <div
          className="fixed pointer-events-none z-50"
          style={{ top: mousePos.y + 10, left: mousePos.x + 10 }}
        >
          <img
            src={conversation.partImageUrl}
            alt=""
            className="max-w-lg max-h-[32rem] border border-gray-300 shadow-lg rounded-md"
          />
        </div>
      )}
    </>
  );
};

const PartVehicleCell = ({ conversation }: { conversation: IPartProConversation }) => {
  if (!conversation.partId && !conversation.vehicleId) {
    return <span className="text-gray-400">—</span>;
  }

  const vehicleLabel = [conversation.vehicleVrm, conversation.vehicleDescription]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-1 items-start">
      {conversation.partId && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openInNewTab(`/stock/${conversation.partId}`);
          }}
          className="text-primary-600 hover:underline text-left whitespace-nowrap"
        >
          {conversation.partTagNumber ? `Tag ${conversation.partTagNumber}` : "View part"}
        </button>
      )}
      {conversation.vehicleId && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openInNewTab(`/vehicles/${conversation.vehicleId}`);
          }}
          className="text-gray-600 hover:underline text-left text-xs whitespace-nowrap"
        >
          {vehicleLabel || "View vehicle"}
        </button>
      )}
    </div>
  );
};

export default function PartProMessages() {
  const { setInfo } = usePageNameContext();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [chosenConversation, setChosenConversation] = useState<IPartProConversation | undefined>();
  const [chainOpen, setChainOpen] = useState(false);

  useEffect(() => {
    setInfo({
      name: "View PartPro Messages",
      desc: "View and manage messages from PartPro buyers.",
    });
  }, []);

  // Drives the query key so toggling the filter refetches (customUrlQuery alone isn't part of it).
  const additionalBody = useMemo(() => ({ unreadOnly }), [unreadOnly]);

  const cols = useMemo(
    () => [
      colHelper.accessor("lastMessageAt", {
        header: "Last Message",
        enableSorting: false,
        cell: (info) => (
          <div>
            <p className="whitespace-nowrap">{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</p>
            <p className="text-sm text-gray-600">{DateTime.fromISO(info.getValue()).toRelative()}</p>
          </div>
        ),
      }),
      colHelper.accessor("buyerName", {
        header: "Buyer",
        enableSorting: false,
        cell: (info) => (
          <span>
            {info.getValue() ?? "Unknown"}
            {info.row.original.lastMessageSender === "VENDOR" && (
              <span className="block text-xs text-gray-500">You replied</span>
            )}
          </span>
        ),
      }),
      colHelper.display({
        id: "thumbnail",
        header: "",
        meta: { thClassName: "!px-2 w-32", tdClassName: "!px-2 w-32" },
        cell: (info) => <ThumbnailCell conversation={info.row.original} />,
      }),
      colHelper.accessor("subject", {
        header: "Listing / Order",
        enableSorting: false,
        cell: (info) => (
          <div>
            <p>{info.row.original.listingTitle ?? info.getValue()}</p>
            {info.row.original.orderNumber && (
              <p className="text-xs text-gray-600">Order {info.row.original.orderNumber}</p>
            )}
          </div>
        ),
      }),
      colHelper.display({
        id: "partVehicle",
        header: "Part / Vehicle",
        cell: (info) => <PartVehicleCell conversation={info.row.original} />,
      }),
      colHelper.accessor("lastMessageBody", {
        header: "Text",
        enableSorting: false,
        cell: (info) => <span className="whitespace-pre-line">{info.getValue()}</span>,
      }),
      colHelper.accessor("unread", {
        header: "Read",
        enableSorting: false,
        cell: (info) => (
          <span className="flex items-center justify-center">{info.getValue() ? "" : "✔"}</span>
        ),
      }),
    ],
    []
  );

  return (
    <div className="mt-4">
      <PartProMessageChain open={chainOpen} setOpen={setChainOpen} conversation={chosenConversation} />

      <div className="flex gap-x-6 items-center">
        <div>
          <label>Unread Only </label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(x) => setUnreadOnly(x.target.checked)}
            className="cursor-pointer"
          />
        </div>
      </div>

      <PagedTable<IPartProConversation>
        url="/partpromessaging/paged"
        columns={cols}
        // customUrlQuery puts the filter in the URL so the backend reads it and the response cache
        // keys on it; additionalBody carries the same value only to change the React Query key.
        customUrlQuery={`unreadOnly=${unreadOnly}`}
        additionalBody={additionalBody}
        onRowClick={(conversation) => {
          setChosenConversation(conversation);
          setChainOpen(true);
        }}
        // PagedTable makes the first class important (`!bg-blue-50`); it's safelisted in tailwind.config.
        rowColor={(conversation) => (conversation.unread ? "bg-blue-50" : "")}
        showTotal
      />
    </div>
  );
}
